import React, { useEffect, useState } from "react";
import {
    MeetingChipList,
    MeetingChipRemove,
    MeetingPanelText,
    MeetingPanelTitle,
    MeetingPrimaryButton,
    MeetingSecondaryButton,
    MeetingStatusCard,
    MeetingTextInput,
    Toast,
} from "src/components";
import {
    endMeetingOccurrence,
    getMeeting,
    getMeetingInvitationDeliveries,
    inviteMeetingGuests,
    MeetingDetail,
    MeetingInvitationDelivery,
    removeMeetingParticipant,
    retryMeetingInvitation,
} from "src/api/meetingsApi";

type MeetingManagementPanelProps = {
    meetingUuid: string;
    onEnded: () => void | Promise<void>;
};

const parseEmails = (value: string): string[] => Array.from(new Set(value
    .split(/[\s,;]+/)
    .map(email => email.trim().toLowerCase())
    .filter(Boolean)));

const getErrorMessage = (error: unknown, fallback: string): string => {
    if (typeof error === "object" && error !== null && "getApiErrorDetails" in error) {
        const apiError = error as { getApiErrorDetails?: () => { message?: string } };
        const message = apiError.getApiErrorDetails?.().message;
        if (message) {
            return message;
        }
    }
    return error instanceof Error && error.message ? error.message : fallback;
};

export default function MeetingManagementPanel({
    meetingUuid,
    onEnded
}: MeetingManagementPanelProps) {
    const [meeting, setMeeting] = useState<MeetingDetail | null>(null);
    const [deliveries, setDeliveries] = useState<MeetingInvitationDelivery[]>([]);
    const [emails, setEmails] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState("");
    const [isSendingInvitations, setIsSendingInvitations] = useState(false);
    const [isEnding, setIsEnding] = useState(false);

    const refresh = () => getMeeting(meetingUuid).then(async nextMeeting => {
        setMeeting(nextMeeting);
        if (nextMeeting.canManage) {
            setDeliveries(await getMeetingInvitationDeliveries(meetingUuid));
        } else {
            setDeliveries([]);
        }
    }).catch(() => undefined);

    useEffect(() => {
        refresh();
        const interval = window.setInterval(refresh, 3000);
        return () => window.clearInterval(interval);
    }, [meetingUuid]);

    const activeParticipants = meeting?.participants.filter(participant => (
        participant.admissionStatus === "admitted" && participant.participantType !== "host" && !participant.leftAt
    )) || [];
    const invitedGuests = meeting?.participants.filter(participant => (
        participant.participantType === "guest" && participant.admissionStatus === "invited"
    )) || [];

    const copyMeetingLink = async (): Promise<void> => {
        try {
            if (!navigator.clipboard) {
                throw new Error("Clipboard access is unavailable in this browser.");
            }
            await navigator.clipboard.writeText(meeting?.meetingUrl || "");
            Toast.success("Meeting link copied.");
        } catch (error) {
            Toast.error(getErrorMessage(error, "Unable to copy the meeting link."));
        }
    };

    const endForEveryone = async (): Promise<void> => {
        if (isEnding) {
            return;
        }
        setIsEnding(true);
        try {
            await endMeetingOccurrence(meetingUuid, meeting.occurrence.occurrenceUuid);
            Toast.success("Call ended for everyone.");
            await onEnded();
        } catch (error) {
            Toast.error(getErrorMessage(error, "Unable to end the meeting."));
        } finally {
            setIsEnding(false);
        }
    };

    const sendInvitations = async (): Promise<void> => {
        if (isSendingInvitations) {
            return;
        }
        const guestEmails = parseEmails(emails);
        if (guestEmails.length === 0) {
            Toast.error("Enter at least one email address.");
            return;
        }
        setIsSendingInvitations(true);
        try {
            await inviteMeetingGuests(meetingUuid, guestEmails, password);
            setEmails("");
            setPassword("");
            const invitationLabel = guestEmails.length === 1 ? "invitation" : "invitations";
            Toast.success(`${guestEmails.length} ${invitationLabel} sent.`);
            await refresh();
        } catch (error) {
            Toast.error(getErrorMessage(error, "Unable to send the invitations."));
        } finally {
            setIsSendingInvitations(false);
        }
    };

    if (!meeting) {
        return <MeetingStatusCard>Loading meeting controls…</MeetingStatusCard>;
    }
    if (!meeting.canManage) {
        return <MeetingStatusCard>Meeting management is available to the host.</MeetingStatusCard>;
    }

    const latestDeliveries = Array.from(deliveries.reduce((latest, delivery) => {
        if (!latest.has(delivery.participantUuid)) {
            latest.set(delivery.participantUuid, delivery);
        }
        return latest;
    }, new Map<string, MeetingInvitationDelivery>()).values());

    return (
        <div style={{ display: "grid", gap: 12 }}>
            <MeetingPanelTitle $dark>Invite & Manage</MeetingPanelTitle>
            <MeetingPanelText $dark>{meeting.title}</MeetingPanelText>
            <MeetingChipList>
                <MeetingSecondaryButton type="button" onClick={copyMeetingLink}>
                    Copy Meeting Link
                </MeetingSecondaryButton>
            </MeetingChipList>
            <MeetingTextInput value={emails} onChange={({ target }) => setEmails(target.value)} placeholder="Guest email addresses" />
            {meeting.passwordEnabled && (
                <>
                    <MeetingTextInput
                        type="password"
                        value={password}
                        onChange={({ target }) => setPassword(target.value)}
                        placeholder="Include current password (optional)"
                    />
                    <MeetingPanelText $dark>
                        Leave this blank to tell guests to use the password previously supplied by the organiser.
                    </MeetingPanelText>
                </>
            )}
            <MeetingPrimaryButton type="button" disabled={isSendingInvitations} onClick={sendInvitations}>
                {isSendingInvitations ? "Sending…" : "Send Invitations"}
            </MeetingPrimaryButton>
            {invitedGuests.length > 0 && (
                <div>
                    <MeetingPanelTitle $dark>Invitations</MeetingPanelTitle>
                    {invitedGuests.map(guest => (
                        <MeetingStatusCard key={guest.participantUuid}>
                            <strong>{guest.email || guest.displayName}</strong> is invited
                        </MeetingStatusCard>
                    ))}
                </div>
            )}
            {latestDeliveries.length > 0 && (
                <div>
                    <MeetingPanelTitle $dark>Delivery Status</MeetingPanelTitle>
                    {latestDeliveries.map(delivery => (
                        <MeetingStatusCard key={delivery.deliveryUuid}>
                            <strong>{delivery.email || delivery.displayName}</strong> · {delivery.status}
                            {delivery.status === "failed" && (
                                <>
                                    {delivery.failureReason && <MeetingPanelText $dark>{delivery.failureReason}</MeetingPanelText>}
                                    <MeetingSecondaryButton
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                await retryMeetingInvitation(meetingUuid, delivery.deliveryUuid);
                                                Toast.success("Invitation queued again.");
                                                await refresh();
                                            } catch (error) {
                                                Toast.error(getErrorMessage(error, "Unable to retry the invitation."));
                                            }
                                        }}
                                    >
                                        Retry
                                    </MeetingSecondaryButton>
                                </>
                            )}
                        </MeetingStatusCard>
                    ))}
                </div>
            )}
            <MeetingPanelTitle $dark>Participants</MeetingPanelTitle>
            {activeParticipants.length > 0 ? activeParticipants.map(participant => (
                <MeetingStatusCard key={participant.participantUuid} $dismissible>
                    <strong>{participant.displayName}</strong> · {participant.participantType === "guest" ? "Guest" : "Me Business user"}
                    <MeetingChipRemove
                        $cardCorner
                        $light
                        type="button"
                        aria-label={`Remove ${participant.displayName} from the meeting`}
                        title={`Remove ${participant.displayName}`}
                        onClick={async () => {
                            await removeMeetingParticipant(meetingUuid, participant.participantUuid);
                            setMessage(`${participant.displayName} was removed.`);
                            await refresh();
                        }}
                    >
                        ×
                    </MeetingChipRemove>
                </MeetingStatusCard>
            )) : <MeetingPanelText $dark>No other participants are connected.</MeetingPanelText>}
            {message && <MeetingStatusCard>{message}</MeetingStatusCard>}
            <MeetingSecondaryButton type="button" disabled={isEnding} onClick={endForEveryone}>
                {isEnding ? "Turning Off Call…" : "Turn Off Call for Everyone"}
            </MeetingSecondaryButton>
        </div>
    );
}
